import { cn } from '@/shared/lib/cn';
import { ALL_CHANNELS, type ChannelSlug, type StayFilter } from './calendarHelpers';
import { PropertyCombobox, type PropertyOption } from '@/shared/components';

// Reihenfolge wie im Board; „Sonstige OTA“ erreicht man über „Alle“.
const CHIP_CHANNELS = (['booking', 'airbnb', 'manual'] as const).map(
  (slug) => ALL_CHANNELS.find((channel) => channel.slug === slug)!,
);

const STAY_LABEL: Record<StayFilter, string> = {
  all: 'Alle',
  current: 'Aktuell',
  future: 'Zukünftig',
};

export interface CalendarToolbarProps {
  propertyOptions: PropertyOption[];
  selectedProperties: Set<string> | null;
  onSelectedPropertiesChange: (next: Set<string> | null) => void;
  enabledChannels: Set<ChannelSlug>;
  onToggleChannel: (slug: ChannelSlug) => void;
  onResetChannels: () => void;
  stayFilter: StayFilter;
  onStayFilterChange: (filter: StayFilter) => void;
  /** `sheet`: gestapelt mit Überschriften und Tippzielen fürs Handy-Blatt. */
  layout?: 'bar' | 'sheet';
}

/**
 * Filter über dem Raster: Aufenthalte (blendet ab, gemessen an heute), Apartments und Kanäle.
 * „Alle“ bei den Kanälen ist der einzige Weg zurück zu „Sonstige OTA“, die keinen eigenen Chip hat.
 */
export function CalendarToolbar({ layout = 'bar', ...props }: CalendarToolbarProps) {
  const sheet = layout === 'sheet';
  const allOn = props.enabledChannels.size === ALL_CHANNELS.length;
  const tall = sheet ? 'h-11' : 'h-8';
  return (
    <div className={cn('flex gap-3', sheet ? 'flex-col' : 'flex-wrap items-center')}>
      {sheet && <Eyebrow>Aufenthalte</Eyebrow>}
      <div
        role="radiogroup"
        aria-label="Aufenthaltsstatus"
        className={cn('rounded-pill bg-card-alt flex gap-0.5 p-1', sheet && 'grid grid-cols-3')}
      >
        {(['all', 'current', 'future'] as const).map((value) => {
          const active = props.stayFilter === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => props.onStayFilterChange(value)}
              className={cn(
                'rounded-pill px-3 text-[13px]',
                tall,
                active ? 'bg-card text-ink font-bold shadow-sm' : 'text-ink-soft font-semibold',
              )}
            >
              {STAY_LABEL[value]}
            </button>
          );
        })}
      </div>
      {sheet && <Eyebrow>Apartment</Eyebrow>}
      <PropertyCombobox
        options={props.propertyOptions}
        selected={props.selectedProperties}
        onChange={props.onSelectedPropertiesChange}
        wide={sheet}
      />
      {!sheet && <span className="grow" />}
      <div className={cn('flex flex-wrap items-center gap-1.5', sheet && 'flex-col items-start')}>
        <Eyebrow className={sheet ? undefined : 'mr-1'}>Kanäle</Eyebrow>
        <div className="flex flex-wrap items-center gap-1.5">
          <ChannelChip label="Alle" active={allOn} onClick={props.onResetChannels} tall={sheet} />
          {CHIP_CHANNELS.map((channel) => (
            <ChannelChip
              key={channel.slug}
              label={channel.label}
              active={props.enabledChannels.has(channel.slug)}
              dotClass={channel.dotClass}
              onClick={() => props.onToggleChannel(channel.slug)}
              tall={sheet}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Eyebrow({ children, className }: { children: string; className?: string }) {
  return (
    <span
      className={cn(
        'text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.14em]',
        className,
      )}
    >
      {children}
    </span>
  );
}

function ChannelChip({
  label,
  active,
  dotClass,
  onClick,
  tall,
}: {
  label: string;
  active: boolean;
  dotClass?: string;
  onClick: () => void;
  tall: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded-pill flex items-center gap-2 px-3 text-[13px] font-semibold',
        tall ? 'h-11' : 'h-9',
        active
          ? 'border-line bg-card text-ink border'
          : 'border-line text-ink-faint border border-dashed',
      )}
    >
      {dotClass && (
        <span aria-hidden className={cn('size-3 rounded-sm', dotClass, !active && 'opacity-30')} />
      )}
      {label}
    </button>
  );
}
